import { create } from 'zustand';
import { supabase, supabaseEnabled } from '../lib/supabase';
import { setAccessToken, clearAccessToken, getAccessToken, SESSION_EXPIRED_EVENT } from '../lib/apiAuth';
import { getBackendBaseUrl } from '../lib/backendUrl';
import { identify, resetAnalytics, track, EVT } from '../lib/analytics';
import { identifySentry } from '../lib/sentry';

function _afterAuthSuccess(user, kind /* 'login' | 'signup' */) {
  try {
    identify(user);
    identifySentry({ id: user?.id, userType: user?.userType });
    track(kind === 'signup' ? EVT.SIGNUP_SUCCESS : EVT.LOGIN_SUCCESS, { user_type: user?.userType });
  } catch { /* noop */ }
}

const AUTH_KEY = 'yenom-auth';
const BACKEND = getBackendBaseUrl();

// Guard so initialize() re-runs never stack duplicate window listeners.
let _sessionExpiredListenerAttached = false;

function mapUser(supabaseUser) {
  if (!supabaseUser) return null;
  const meta = supabaseUser.user_metadata || {};
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    phone: supabaseUser.phone || null,
    name: meta.name || meta.full_name || supabaseUser.email?.split('@')[0] || (supabaseUser.phone || '') || '',
    userType: meta.userType || 'retail',
    institutionalRole: meta.institutionalRole || 'Admin',
    createdAt: supabaseUser.created_at,
    hasCompletedOnboarding: true,
  };
}

/** Call the backend once after any Supabase sign-in to upsert the Mongo shadow
 *  user and pull back the canonical profile (user_type etc.). Returns the
 *  server's user dict or null on failure (caller should keep the Supabase-mapped
 *  fallback in that case). */
async function bootstrapSupabaseSession(accessToken) {
  if (!BACKEND || !accessToken) return null;
  try {
    const res = await fetch(`${BACKEND}/api/auth/bootstrap`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: '{}',
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.user) return normalizeApiUser(data.user);
  } catch { /* network error — fall back */ }
  return null;
}

function saveLocal(user, accessToken) {
  try {
    localStorage.setItem(AUTH_KEY, JSON.stringify({ user, accessToken: accessToken || getAccessToken() || undefined }));
    if (accessToken) setAccessToken(accessToken);
  } catch {}
}

function clearLocal() {
  try { localStorage.removeItem(AUTH_KEY); } catch {}
  clearAccessToken();
}

function loadLocal() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.accessToken) setAccessToken(parsed.accessToken);
      return normalizeApiUser(parsed.user) || parsed.user || null;
    }
  } catch {}
  return null;
}

/** Normalize Mongo/local API user shape to camelCase for the UI. */
export function normalizeApiUser(u) {
  if (!u) return null;
  const authProvider = u.auth_provider || u.authProvider || null;
  const confirmed = u.account_type_confirmed ?? u.accountTypeConfirmed;
  return {
    id: u.id,
    email: u.email,
    phone: u.phone || null,
    name: u.name || u.email?.split('@')[0] || (u.phone || ''),
    userType: u.user_type || u.userType || 'retail',
    institutionalRole: u.institutional_role || u.institutionalRole || 'Admin',
    authProvider,
    accountTypeConfirmed: confirmed ?? !['google', 'phone'].includes(authProvider),
    createdAt: u.created_at || u.createdAt,
    hasCompletedOnboarding: u.has_completed_onboarding !== false && u.hasCompletedOnboarding !== false,
  };
}

export function needsAccountTypeChoice(user) {
  if (!user) return false;
  return user.accountTypeConfirmed === false;
}

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Seeded test accounts (see scripts/seed-sample-data.py). Used ONLY as defaults
// when the backend is unreachable and we fall back to an offline UI session so
// you can still exercise the correct surface (advisor vs retail) while Mongo is
// down. Real logins always use the user_type returned by the API.
const TEST_EMAIL_ROLES = {
  'anupam@kubair.co': { userType: 'institutional', institutionalRole: 'Admin' },
  'priya@kubair.co':  { userType: 'institutional', institutionalRole: 'Admin' },
  'rahul@kubair.co':  { userType: 'institutional', institutionalRole: 'member' },
  'sneha@kubair.co':  { userType: 'retail',        institutionalRole: 'member' },
  'vikram@kubair.co': { userType: 'retail',        institutionalRole: 'viewer' },
};

// Remember the last successfully-authenticated user_type per email, keyed in
// localStorage. Lets the offline fallback preserve institutional sessions
// instead of silently downgrading everyone to retail when Mongo is down.
const ROLE_HINTS_KEY = 'yenom-role-hints';

function readRoleHint(email) {
  try {
    const raw = localStorage.getItem(ROLE_HINTS_KEY);
    if (!raw) return null;
    const map = JSON.parse(raw) || {};
    return map[email?.toLowerCase()] || null;
  } catch { return null; }
}

export function writeRoleHint(email, userType, institutionalRole) {
  if (!email || !userType) return;
  try {
    const raw = localStorage.getItem(ROLE_HINTS_KEY);
    const map = raw ? JSON.parse(raw) || {} : {};
    map[email.toLowerCase()] = { userType, institutionalRole: institutionalRole || 'Admin' };
    localStorage.setItem(ROLE_HINTS_KEY, JSON.stringify(map));
  } catch { /* noop */ }
}

function localLogin(email, name) {
  const key = (email || '').toLowerCase();
  const hint = readRoleHint(key);
  const seed = TEST_EMAIL_ROLES[key];
  const role = hint || seed || { userType: 'retail', institutionalRole: 'Admin' };
  return {
    id: generateId(),
    email,
    name: name || email.split('@')[0],
    userType: role.userType,
    institutionalRole: role.institutionalRole,
    createdAt: new Date().toISOString(),
    hasCompletedOnboarding: true,
  };
}

export const useAuthStore = create((set, get) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  defaultWorkspace: 'public',

  initialize: async () => {
    // Expired bearer + failed refresh (see apiAuth interceptor): log out cleanly
    // instead of letting pages render empty data with a stale session.
    // Attach once — initialize() can run multiple times (React StrictMode,
    // re-initialization) and listeners must not accumulate.
    if (!_sessionExpiredListenerAttached) {
      _sessionExpiredListenerAttached = true;
      try {
        window.addEventListener(SESSION_EXPIRED_EVENT, () => {
          if (get().isAuthenticated) get().logout();
        });
      } catch { /* noop — SSR / test env */ }
    }

    const cached = loadLocal();
    if (cached?.id) {
      try { identify(cached); identifySentry({ id: cached.id, userType: cached.userType }); } catch { /* noop */ }
    }
    if (cached) {
      // With Supabase off we use API login (Mongo) + JWT, or local-only sessions. When the backend
      // has YENOM_REQUIRE_API_AUTH=false, routes still work without a Bearer token; clearing the UI
      // session here made "login" appear broken after refresh or after the offline fallback.
      if (!supabaseEnabled && !getAccessToken() && process.env.REACT_APP_REQUIRE_JWT_FOR_SESSION === 'true') {
        clearLocal();
        set({ user: null, isAuthenticated: false, isLoading: false });
        return;
      }
      if (!supabaseEnabled) {
        set({ user: cached, isAuthenticated: true, isLoading: false });
        return;
      }
      // Supabase mode: do NOT mark authenticated yet. The cached bearer in
      // localStorage may be expired (Supabase access tokens live ~1h); if we
      // render authed pages now, their first API calls 401 and can trip the
      // session-expired logout before getSession() below installs a fresh
      // token. Await the fresh session first, then authenticate.
    }

    if (!supabaseEnabled) {
      set({ isLoading: false });
      return;
    }

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user && session?.access_token) {
        setAccessToken(session.access_token);
        const fallback = mapUser(session.user);
        const canonical = await bootstrapSupabaseSession(session.access_token);
        const user = canonical || fallback;
        saveLocal(user, session.access_token);
        set({ user, isAuthenticated: true, isLoading: false });
      } else if (!cached) {
        set({ user: null, isAuthenticated: false, isLoading: false });
      } else {
        // Cached user but no live Supabase session (e.g. offline): keep the
        // cached UI session rather than forcing a re-login.
        set({ user: cached, isAuthenticated: true, isLoading: false });
      }

      supabase.auth.onAuthStateChange(async (event, session) => {
        if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') && session?.user && session?.access_token) {
          setAccessToken(session.access_token);
          const fallback = mapUser(session.user);
          // Only bootstrap on SIGNED_IN (first session); TOKEN_REFRESHED just
          // rotates the JWT and shouldn't re-hit the backend upsert.
          const canonical = event === 'SIGNED_IN'
            ? (await bootstrapSupabaseSession(session.access_token))
            : null;
          const user = canonical || fallback;
          saveLocal(user, session.access_token);
          set({ user, isAuthenticated: true });
          if (event === 'SIGNED_IN') _afterAuthSuccess(user, 'login');
        } else if (event === 'SIGNED_OUT') {
          clearLocal();
          set({ user: null, isAuthenticated: false });
        }
        // Ignore INITIAL_SESSION, USER_UPDATED, PASSWORD_RECOVERY etc.
      });
    } catch {
      // getSession() threw — fall back to the cached session if we have one
      // so a transient Supabase error doesn't strand the user on the login page.
      if (cached) set({ user: cached, isAuthenticated: true, isLoading: false });
      else set({ isLoading: false });
    }
  },

  login: async (email, password) => {
    set({ isLoading: true });

    if (!supabaseEnabled) {
      if (!BACKEND) {
        const user = localLogin(email);
        saveLocal(user);
        set({ user, isAuthenticated: true, isLoading: false });
        return { success: true };
      }
      try {
        const res = await fetch(`${BACKEND}/api/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.user) {
          const user = normalizeApiUser(data.user);
          const accessToken = data.access_token;
          writeRoleHint(user.email, user.userType, user.institutionalRole);
          saveLocal(user, accessToken);
          set({ user, isAuthenticated: true, isLoading: false });
          return { success: true };
        }
        if (res.ok && !data.user) {
          const ct = (res.headers.get('content-type') || '').toLowerCase();
          set({ isLoading: false });
          return {
            success: false,
            error: ct.includes('text/html')
              ? 'Login hit the web app instead of the API. Set REACT_APP_BACKEND_URL=http://127.0.0.1:8000 in frontend/.env (or symlink to root .env) and restart npm start.'
              : 'Unexpected response from server. Is the API running on port 8000?',
          };
        }
        const d = data.detail;
        const detailStr =
          typeof d === 'string' ? d
          : Array.isArray(d) && d[0]?.msg ? String(d[0].msg)
          : '';
        const errMsg =
          res.status === 401 && detailStr.toLowerCase().includes('not found')
            ? 'No account for this email. Use Sign up first, then sign in.'
            : detailStr || 'Invalid email or password.';
        set({ isLoading: false });
        return { success: false, error: errMsg };
      } catch {
        /* backend unreachable — offline-only session (no JWT; use YENOM_REQUIRE_API_AUTH=false on API) */
        const user = localLogin(email);
        saveLocal(user);
        set({ user, isAuthenticated: true, isLoading: false });
        return { success: true };
      }
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        set({ isLoading: false });
        return { success: false, error: error.message };
      }
      if (data.session?.access_token) setAccessToken(data.session.access_token);
      const fallback = mapUser(data.user);
      const canonical = await bootstrapSupabaseSession(data.session?.access_token);
      const user = canonical || fallback;
      saveLocal(user, data.session?.access_token);
      set({ user, isAuthenticated: true, isLoading: false });
      _afterAuthSuccess(user, 'login');
      return { success: true };
    } catch (err) {
      set({ isLoading: false });
      return { success: false, error: 'Login failed. Please try again.' };
    }
  },

  signup: async (name, email, password, userType = 'retail', institutionalRole = 'Admin', extras = {}) => {
    set({ isLoading: true });
    const orgPayload = extras?.org || null;
    const panPayload = extras?.pan || null;
    // Stash institutional org data locally so the post-auth onboarding wizard can
    // pick it up without requiring backend changes to the signup endpoint.
    if (orgPayload) {
      try { localStorage.setItem('yenom-pending-org', JSON.stringify(orgPayload)); } catch { /* noop */ }
    }

    if (!supabaseEnabled) {
      if (!BACKEND) {
        const user = { ...localLogin(email, name), userType, institutionalRole };
        writeRoleHint(user.email, userType, institutionalRole);
        saveLocal(user);
        set({ user, isAuthenticated: true, isLoading: false });
        return { success: true };
      }
      try {
        const res = await fetch(`${BACKEND}/api/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name,
            email,
            password,
            user_type: userType,
            institutional_role: institutionalRole,
            pan: panPayload || undefined,
            org: orgPayload || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.user) {
          const user = normalizeApiUser(data.user);
          writeRoleHint(user.email, user.userType, user.institutionalRole);
          saveLocal(user, data.access_token);
          set({ user, isAuthenticated: true, isLoading: false });
          return { success: true };
        }
        if (res.ok && !data.user) {
          const ct = (res.headers.get('content-type') || '').toLowerCase();
          set({ isLoading: false });
          return {
            success: false,
            error: ct.includes('text/html')
              ? 'Signup hit the web app instead of the API. Set REACT_APP_BACKEND_URL=http://127.0.0.1:8000 and restart npm start.'
              : 'Unexpected response from server.',
          };
        }
        const detail = data.detail;
        set({ isLoading: false });
        return {
          success: false,
          error: typeof detail === 'string' ? detail : 'Signup failed. Please try again.',
        };
      } catch {
        const user = { ...localLogin(email, name), userType, institutionalRole };
        saveLocal(user);
        set({ user, isAuthenticated: true, isLoading: false });
        return { success: true };
      }
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            userType,
            institutionalRole,
            pan: panPayload || undefined,
            pendingOrg: orgPayload || undefined,
          },
        },
      });

      if (error) {
        set({ isLoading: false });
        const msg = error.message?.toLowerCase().includes('already')
          ? 'This email is already registered. Please sign in instead.'
          : error.message?.toLowerCase().includes('password')
          ? 'Password must be at least 6 characters.'
          : error.message || 'Signup failed. Please try again.';
        return { success: false, error: msg };
      }

      // Email confirmation required
      if (data.user && !data.session) {
        set({ isLoading: false });
        return { success: true, confirmEmail: true };
      }

      // Signup succeeded with session
      if (data.user && data.session) {
        const user = mapUser(data.user);
        saveLocal(user);
        set({ user, isAuthenticated: true, isLoading: false });
        return { success: true };
      }

      // Fallback: no user returned (shouldn't happen)
      set({ isLoading: false });
      return { success: false, error: 'Unexpected error. Please try again.' };
    } catch (err) {
      set({ isLoading: false });
      return { success: false, error: 'Signup failed. Please try again.' };
    }
  },

  /** Start a Supabase Google OAuth flow. Returns {success, error}. On success
   *  the browser is redirected to Google and eventually back to
   *  /auth/callback, where SIGNED_IN fires via onAuthStateChange. */
  loginWithGoogle: async () => {
    if (!supabaseEnabled) {
      return { success: false, error: 'Google sign-in is not configured. Ask the admin to set Supabase env vars.' };
    }
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (error) return { success: false, error: error.message };
      return { success: true, redirecting: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Could not start Google sign-in.' };
    }
  },

  /** Send a phone OTP (SMS). `phone` must be E.164 (e.g. "+919876543210"). */
  sendPhoneOtp: async (phone) => {
    if (!supabaseEnabled) {
      return { success: false, error: 'Phone sign-in is not configured.' };
    }
    try {
      const { error } = await supabase.auth.signInWithOtp({
        phone,
        options: { channel: 'sms' },
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Could not send OTP.' };
    }
  },

  /** Verify a phone OTP. On success, a session is created and the user is signed in. */
  verifyPhoneOtp: async (phone, token) => {
    if (!supabaseEnabled) {
      return { success: false, error: 'Phone sign-in is not configured.' };
    }
    set({ isLoading: true });
    try {
      const { data, error } = await supabase.auth.verifyOtp({ phone, token, type: 'sms' });
      if (error) {
        set({ isLoading: false });
        return { success: false, error: error.message };
      }
      if (data.session?.access_token) setAccessToken(data.session.access_token);
      const fallback = mapUser(data.user);
      const canonical = await bootstrapSupabaseSession(data.session?.access_token);
      const user = canonical || fallback;
      saveLocal(user, data.session?.access_token);
      set({ user, isAuthenticated: true, isLoading: false });
      _afterAuthSuccess(user, 'login');
      return { success: true };
    } catch (err) {
      set({ isLoading: false });
      return { success: false, error: err?.message || 'Could not verify OTP.' };
    }
  },

  setAccountType: async (userType, orgName) => {
    if (!BACKEND) return { success: false, error: 'Backend is not configured.' };

    let token = getAccessToken();
    // Supabase sessions are persisted by supabase-js itself; the bearer in
    // `yenom-access-token` can drift if the page survived a token refresh.
    // Pull the freshest token before we hit the API to avoid empty-bearer
    // requests, which the backend turns into 500s with no CORS headers and
    // Safari surfaces as the unhelpful "Load failed".
    if (!token && supabaseEnabled) {
      try {
        const { data } = await supabase.auth.getSession();
        if (data?.session?.access_token) {
          token = data.session.access_token;
          setAccessToken(token);
        }
      } catch { /* ignore — handled below */ }
    }
    // No hard client-side gate on the token: in local dev the API accepts
    // unauthenticated calls (YENOM_REQUIRE_API_AUTH=false), and in prod the
    // server answers 401 which we surface below. Refusing to call at all left
    // users stuck with a stale session and no way forward.
    let res;
    try {
      res = await fetch(`${BACKEND}/api/auth/account-type`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          user_type: userType,
          ...(orgName ? { org_name: orgName } : {}),
        }),
      });
    } catch (err) {
      return {
        success: false,
        error: `Network error reaching the API (${err?.message || 'fetch failed'}). Check your connection and try again.`,
      };
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = typeof data?.detail === 'string' ? data.detail : '';
      if (res.status === 401) {
        return { success: false, error: 'Session expired. Please sign in again.' };
      }
      return {
        success: false,
        error: detail || `Could not save account type (HTTP ${res.status}).`,
      };
    }
    if (!data.user) {
      return { success: false, error: 'Server response missing user profile. Try again in a moment.' };
    }
    const user = normalizeApiUser(data.user);
    writeRoleHint(user.email, user.userType, user.institutionalRole);
    saveLocal(user);
    set({ user, isAuthenticated: true });
    return { success: true, user, org: data.org || null };
  },

  /** Email a password-reset link. Supabase returns success even for unknown
   *  emails (to avoid leaking existence). UI should show a neutral message. */
  requestPasswordReset: async (email) => {
    if (!supabaseEnabled) {
      return { success: false, error: 'Password reset is not configured. Contact support.' };
    }
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Could not send reset link.' };
    }
  },

  /** Complete a password reset. Must be called from the /reset-password page
   *  after Supabase's recovery redirect established a session. */
  setNewPassword: async (password) => {
    if (!supabaseEnabled) {
      return { success: false, error: 'Password reset is not configured.' };
    }
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: err?.message || 'Could not update password.' };
    }
  },

  logout: async () => {
    clearLocal();
    resetAnalytics();
    set({ user: null, isAuthenticated: false });
    if (supabaseEnabled) {
      await supabase.auth.signOut().catch(() => {});
    }
  },

  updateUser: async (updates) => {
    const current = get().user;
    if (!supabaseEnabled) {
      if (current) {
        const user = { ...current, ...updates };
        saveLocal(user);
        set({ user });
      }
      return;
    }
    const { data, error } = await supabase.auth.updateUser({ data: updates });
    if (!error && data.user) {
      const user = mapUser(data.user);
      saveLocal(user);
      set({ user });
    }
  },
}));
